import type { Interpable, Lerpable } from '@utils';
import { expectDefined } from '@utils';
import { composeStruct } from '@strings';
import { z } from 'zod';
import { Angle, AngleSchema } from './Angle';
import type { IPath, ParamPoint } from './IPath';
import { LineSegment, projectPointOntoLine } from './LineSegment';
import { Point, PointSchema } from './Point';
import { Rect } from './Rect';
import { lineSegment, point } from './constructors';
import {
  endpointsEllipseFromSVGPathData,
  fromCenterEllipse,
  svgPathDataFromEndpointsEllipse,
  type CenterEllipse,
  type EndpointsEllipse
} from './ellipseParameterizations';

/** Ellipse2: also represents a circle and an arc.
 * The point on the ellipse corresponding to the parameter `t` is calculated by
 * the formula `center + cos(t) * aAxis + sin(t) * bAxis`.
 * To get an ellipse rotating clockwise use, for example,
 * `aAxis = (-1, 0)` and `bAxis = (0,1)` */

// #region Schema

export const EllipseSchema = z
  .tuple([PointSchema, PointSchema, PointSchema, AngleSchema, z.boolean(), z.boolean()])
  .transform(([start, end, radii, xRotation, largeArc, sweep]) =>
    ellipse(start, end, radii, xRotation, largeArc, sweep)
  );

// #endregion

export function ellipse(
  start: Point,
  end: Point,
  radii: Point,
  xRotation: Angle,
  largeArc: boolean,
  sweep: boolean
) {
  return new Ellipse(start, end, radii, xRotation, largeArc, sweep);
}

export class Ellipse implements EndpointsEllipse, IPath, Lerpable, Interpable {
  readonly #start: Point;
  readonly #end: Point;
  readonly #radii: Point;
  readonly #xRotation: Angle;
  readonly #largeArc: boolean;
  readonly #sweep: boolean;

  // #region Constructors

  constructor(
    start: Point,
    end: Point,
    radii: Point,
    xRotation: Angle,
    largeArc: boolean,
    sweep: boolean
  ) {
    this.#start = start;
    this.#end = end;
    this.#radii = radii;
    this.#xRotation = xRotation;
    this.#largeArc = largeArc;
    this.#sweep = sweep;
  }

  /**
   * Creates an `Ellipse2` instance from the given `EndpointsEllipse` object.
   *
   * @param endpointsEllipse - The `EndpointsEllipse` object containing the endpoints of the ellipse.
   * @returns A new `Ellipse2` instance with properties derived from the `EndpointsEllipse`.
   */
  static fromEndpointsEllipse(endpointsEllipse: EndpointsEllipse): Ellipse {
    return ellipse(
      endpointsEllipse.start,
      endpointsEllipse.end,
      endpointsEllipse.radii,
      endpointsEllipse.xRotation,
      endpointsEllipse.largeArc,
      endpointsEllipse.sweep
    );
  }

  /**
   * Creates an `Ellipse2` instance from a `CenterEllipse` object.
   *
   * @param centerEllipse - The `CenterEllipse` object containing the properties to create the `Ellipse2`.
   * @returns A new `Ellipse2` instance.
   */
  static fromCenterEllipse(centerEllipse: CenterEllipse): Ellipse {
    const endpointsEllipse = fromCenterEllipse(centerEllipse);
    return ellipse(
      endpointsEllipse.start,
      endpointsEllipse.end,
      endpointsEllipse.radii,
      endpointsEllipse.xRotation,
      endpointsEllipse.largeArc,
      endpointsEllipse.sweep
    );
  }

  // #endregion

  // #region Attributes

  get start(): Point {
    return this.#start;
  }
  get end(): Point {
    return this.#end;
  }
  get radii(): Point {
    return this.#radii;
  }
  get xRotation(): Angle {
    return this.#xRotation;
  }
  get largeArc(): boolean {
    return this.#largeArc;
  }
  get sweep(): boolean {
    return this.#sweep;
  }

  get isCounterclockwise(): boolean {
    return this.sweep;
  }

  public get center(): Point {
    return point((this.start.x + this.end.x) / 2, (this.start.y + this.end.y) / 2);
  }

  // #endregion

  private _cache:
    | {
        length?: number;
        bounds?: Rect;
        lut?: Point[];
      }
    | undefined;

  static LUT_INTERVAL = 10;
  static LUT_MIN_SAMPLES = 5;

  recalculateCache() {
    this._cache = {
      length: this._length,
      bounds: this._bounds,
      lut: this._lut
    };
  }

  // #region Comparison

  equals(other: Ellipse): boolean {
    return (
      this === other ||
      (this.start.equals(other.start) &&
        this.end.equals(other.end) &&
        this.radii.equals(other.radii) &&
        this.xRotation.equals(other.xRotation) &&
        this.largeArc === other.largeArc &&
        this.sweep === other.sweep)
    );
  }

  isSamePath(other: IPath): boolean {
    return other instanceof Ellipse && this.equals(other);
  }

  isIsomorphicTo(other: IPath): boolean {
    return other instanceof Ellipse;
  }

  // #endregion

  // #region Geometric Operations

  private get _length(): number {
    const a = this.radii.x; // Semi-major axis
    const b = this.radii.y; // Semi-minor axis
    const phi = this.xRotation.radians; // Rotation angle
    const nIntervals = 100; // Number of intervals for numerical integration

    // Adjust start and end angles considering sweep flag
    const { startAngle, deltaAngle, direction } = this.getSweepAdjustedAngles();
    const h = deltaAngle / nIntervals; // h is positive

    let sum =
      _speed(a, b, startAngle, phi) +
      _speed(a, b, startAngle + direction * deltaAngle, phi);

    for (let i = 1; i < nIntervals; i++) {
      const angle = startAngle + direction * i * h;
      const weight = i % 2 === 0 ? 2 : 4;
      sum += weight * _speed(a, b, angle, phi);
    }

    return (h / 3) * sum;
  }

  private getSweepAdjustedAngles(): {
    startAngle: number;
    endAngle: number;
    deltaAngle: number;
    direction: number;
  } {
    const startAngle = this.parametricAngle(this.start);
    const endAngle = this.parametricAngle(this.end);
    let deltaAngle = endAngle - startAngle;
    if (this.sweep && deltaAngle < 0) {
      deltaAngle += 2 * Math.PI;
    } else if (!this.sweep && deltaAngle > 0) {
      deltaAngle -= 2 * Math.PI;
    }
    const direction = deltaAngle >= 0 ? 1 : -1;
    deltaAngle = Math.abs(deltaAngle);
    return { startAngle, endAngle, deltaAngle, direction };
  }

  /**
   * Computes the parametric angle corresponding to a point on the ellipse.
   *
   * @param p - The point on the ellipse.
   * @returns The parametric angle in radians.
   */
  private parametricAngle(p: Point): number {
    const { center, rx, ry } = this.computeCenterAndRadii();
    const phi = this.xRotation.radians;
    const cosPhi = Math.cos(phi);
    const sinPhi = Math.sin(phi);

    // Compute (x', y')
    const dx = p.x - center.x;
    const dy = p.y - center.y;

    const xPrime = (dx * cosPhi + dy * sinPhi) / rx;
    const yPrime = (-dx * sinPhi + dy * cosPhi) / ry;

    // Compute angle
    const angle = Math.atan2(yPrime, xPrime);

    return angle;
  }

  private parametricAngle2(p: Point, radii: Point, phi: number): number {
    const rx = radii.x;
    const ry = radii.y;

    // Transform point to parametric space
    const xPrime = (p.x * Math.cos(-phi) - p.y * Math.sin(-phi)) / rx;
    const yPrime = (p.x * Math.sin(-phi) + p.y * Math.cos(-phi)) / ry;

    return Math.atan2(yPrime, xPrime);
  }

  get length(): number {
    if (this._cache?.length === undefined) {
      const length = this._length;
      this._cache = { ...(this._cache ?? {}), length };
    }
    return expectDefined(this._cache.length);
  }

  private get _bounds(): Rect {
    // The LUT includes points from t=0 to t=1, so it covers
    // the actual (potentially large) arc that’s being drawn.
    return Rect.fromPoints(this.lut);
  }

  private getCriticalTs(): number[] {
    const ts: number[] = [];
    const { radii, xRotation } = this;
    const rx = radii.x;
    const ry = radii.y;
    const phi = xRotation.radians;
    const sinPhi = Math.sin(phi);
    const cosPhi = Math.cos(phi);

    // Critical points where dx/dt = 0
    const tan_t1_num = ry * sinPhi;
    const tan_t1_den = rx * cosPhi;
    const t1 = Math.atan2(tan_t1_num, tan_t1_den);
    ts.push(t1, t1 + Math.PI);

    // Critical points where dy/dt = 0
    const tan_t2_num = -ry * cosPhi;
    const tan_t2_den = rx * sinPhi;
    const t2 = Math.atan2(tan_t2_num, tan_t2_den);
    ts.push(t2, t2 + Math.PI);

    // Map t's to [0, 2π) and filter to those within the arc
    const criticalTs: number[] = [];
    for (const tAngle of ts) {
      const angle = (tAngle + 2 * Math.PI) % (2 * Math.PI);
      if (this.isAngleWithinArc(angle)) {
        const t = this.parametricTFromAngle(angle);
        if (t >= 0 && t <= 1) {
          criticalTs.push(t);
        }
      }
    }

    return criticalTs;
  }

  private isAngleWithinArc(tAngle: number): boolean {
    const { startAngle, endAngle, deltaAngle } = this.getSweepAdjustedAngles();

    // Normalize angles to [0, 2π)
    const normalizedStart = (startAngle + 2 * Math.PI) % (2 * Math.PI);
    const normalizedEnd = (endAngle + 2 * Math.PI) % (2 * Math.PI);
    const normalizedT = (tAngle + 2 * Math.PI) % (2 * Math.PI);

    if (deltaAngle >= 0) {
      if (normalizedStart <= normalizedEnd) {
        return normalizedStart <= normalizedT && normalizedT <= normalizedEnd;
      } else {
        return normalizedT >= normalizedStart || normalizedT <= normalizedEnd;
      }
    } else {
      if (normalizedEnd <= normalizedStart) {
        return normalizedEnd <= normalizedT && normalizedT <= normalizedStart;
      } else {
        return normalizedT >= normalizedEnd || normalizedT <= normalizedStart;
      }
    }
  }

  private parametricTFromAngle(tAngle: number): number {
    const { startAngle, deltaAngle } = this.getSweepAdjustedAngles();
    let angleDiff = tAngle - startAngle;

    // Adjust angleDiff based on deltaAngle
    if (deltaAngle >= 0 && angleDiff < 0) {
      angleDiff += 2 * Math.PI;
    } else if (deltaAngle < 0 && angleDiff > 0) {
      angleDiff -= 2 * Math.PI;
    }

    return angleDiff / deltaAngle;
  }

  get bounds(): Rect {
    if (this._cache?.bounds === undefined) {
      const bounds = this._bounds;
      this._cache = { ...(this._cache ?? {}), bounds };
    }
    return expectDefined(this._cache.bounds);
  }

  private get _lut(): Point[] {
    const numSamples = Math.max(
      Math.floor(this.length / Ellipse.LUT_INTERVAL),
      Ellipse.LUT_MIN_SAMPLES
    );

    const points: Point[] = [];
    for (let i = 0; i <= numSamples; i++) {
      const t = i / numSamples;
      points.push(this.pointAt(t));
    }

    return points;
  }

  get lut(): Point[] {
    if (this._cache?.lut === undefined) {
      const lut = this._lut;
      this._cache = { ...(this._cache ?? {}), lut };
    }
    return expectDefined(this._cache.lut);
  }

  reversed(): this {
    return ellipse(
      this.end,
      this.start,
      this.radii,
      this.xRotation,
      this.largeArc,
      !this.sweep
    ) as this;
  }

  flattened(flatness: number): ParamPoint[] {
    const result: ParamPoint[] = [{ p: this.pointAt(0), t: 0 }];
    this.flatten(0, 1, flatness, result);
    return result;
  }

  private flatten(t0: number, t1: number, tolerance: number, result: ParamPoint[]) {
    const p0 = this.pointAt(t0);
    const p1 = this.pointAt(t1);
    const tMid = (t0 + t1) / 2;
    const pMid = this.pointAt(tMid);

    const deviation = projectPointOntoLine(pMid, p0, p1).d;

    if (deviation < tolerance) {
      result.push({ p: p1, t: t1 });
    } else {
      this.flatten(t0, tMid, tolerance, result);
      this.flatten(tMid, t1, tolerance, result);
    }
  }

  /**
   * Computes the derivative of the ellipse at parameter t.
   *
   * @param t - The parameter along the ellipse, where 0 <= t <= 1.
   * @returns The derivative as a `Point`.
   */
  derivative(t: number): Point {
    const { rx, ry } = this.computeCenterAndRadii();
    const phi = this.xRotation.radians;
    const cosPhi = Math.cos(phi);
    const sinPhi = Math.sin(phi);

    // Parametric angles
    const startAngle = this.parametricAngle(this.start);
    const endAngle = this.parametricAngle(this.end);

    // Compute deltaAngle considering sweep flag
    let deltaAngle = endAngle - startAngle;
    if (this.sweep && deltaAngle < 0) {
      deltaAngle += 2 * Math.PI;
    } else if (!this.sweep && deltaAngle > 0) {
      deltaAngle -= 2 * Math.PI;
    }

    const angle = startAngle + t * deltaAngle;
    const sinAngle = Math.sin(angle);
    const cosAngle = Math.cos(angle);

    // Compute derivative in the rotated coordinate system
    const dxdt = deltaAngle * (-rx * sinAngle);
    const dydt = deltaAngle * (ry * cosAngle);

    // Rotate derivative back to original coordinate system
    const dx = dxdt * cosPhi - dydt * sinPhi;
    const dy = dxdt * sinPhi + dydt * cosPhi;

    return point(dx, dy);
  }

  /**
   * Computes the center and adjusted radii of the ellipse based on the current parameters.
   * This method does not modify any class fields and returns the computed values.
   */
  private computeCenterAndRadii(): { center: Point; rx: number; ry: number } {
    const x1 = this.start.x;
    const y1 = this.start.y;
    const x2 = this.end.x;
    const y2 = this.end.y;
    let rx = Math.abs(this.radii.x);
    let ry = Math.abs(this.radii.y);
    const phi = this.xRotation.radians;
    const largeArcFlag = this.largeArc ? 1 : 0;
    const sweepFlag = this.sweep ? 1 : 0;

    // Step 1: Compute (x1', y1')
    const dx = (x1 - x2) / 2;
    const dy = (y1 - y2) / 2;
    const cosPhi = Math.cos(phi);
    const sinPhi = Math.sin(phi);

    const x1p = cosPhi * dx + sinPhi * dy;
    const y1p = -sinPhi * dx + cosPhi * dy;

    // Step 2: Ensure radii are large enough
    const x1pSq = x1p * x1p;
    const y1pSq = y1p * y1p;
    let rxSq = rx * rx;
    let rySq = ry * ry;
    const radiiCheck = x1pSq / rxSq + y1pSq / rySq;
    if (radiiCheck > 1) {
      const scale = Math.sqrt(radiiCheck);
      rx *= scale;
      ry *= scale;
      rxSq = rx * rx;
      rySq = ry * ry;
      // Adjusted radii are used locally
    }

    // Step 3: Compute (cx', cy')
    const sign = largeArcFlag !== sweepFlag ? 1 : -1;
    const numerator = rxSq * rySq - rxSq * y1pSq - rySq * x1pSq;
    const denominator = rxSq * y1pSq + rySq * x1pSq;
    const c = sign * Math.sqrt(Math.max(0, numerator / denominator));

    const cxp = (c * (rx * y1p)) / ry;
    const cyp = (c * (-ry * x1p)) / rx;

    // Step 4: Compute (cx, cy)
    const cx = cosPhi * cxp - sinPhi * cyp + (x1 + x2) / 2;
    const cy = sinPhi * cxp + cosPhi * cyp + (y1 + y2) / 2;

    return { center: point(cx, cy), rx, ry };
  }

  private speed(t: number): number {
    return this.derivative(t).magnitude;
  }

  normal(t: number): Point {
    return this.derivative(t).perpendicular().normalized();
  }

  angle(t: number): Angle {
    return this.derivative(t).angleWithOrigin;
  }

  project(p: Point): { p: Point; t: number; d: number } {
    // Approximate projection using LUT
    let closestPoint = this.lut[0];
    let closestT = 0;
    let minDistSq = p.sub(closestPoint).hypotSq;
    const numPoints = this.lut.length;

    for (let i = 1; i < numPoints; i++) {
      const lutP = this.lut[i];
      const dSq = p.sub(lutP).hypotSq;
      if (dSq < minDistSq) {
        minDistSq = dSq;
        closestPoint = lutP;
        closestT = i / (numPoints - 1);
      }
    }

    const d = Math.sqrt(minDistSq);
    return { p: closestPoint, t: closestT, d };
  }

  intersectsRect(r: Rect): boolean {
    // First, check if either endpoint is inside the rectangle
    if (r.containsPoint(this.start) || r.containsPoint(this.end)) {
      return true;
    }

    // If bounding boxes do not overlap, no intersection is possible
    if (!r.intersects(this.bounds)) {
      return false;
    }

    // Check for containment of interpolated points
    const expandedRect = r.expandingBy(Ellipse.LUT_INTERVAL);
    if (this.lut.some(p => expandedRect.containsPoint(p))) {
      return true;
    }

    // No intersection
    return false;
  }

  intersectsLineSegment(line: LineSegment): boolean {
    return this.intersectionsWithLineSegment(line).length > 0;
  }

  intersectionsWithLineSegment(line: LineSegment): number[] {
    // Approximate intersection by checking LUT points
    const intersections: number[] = [];
    const numPoints = this.lut.length;
    for (let i = 1; i < numPoints; i++) {
      const segment = lineSegment(this.lut[i - 1], this.lut[i]);
      if (segment.intersects(line)) {
        intersections.push((i - 1) / (numPoints - 1));
      }
    }
    return intersections;
  }

  lengthAt(t: number): number {
    // Approximate length at t using LUT
    const lut = this.lut;
    const targetIndex = Math.floor(t * (lut.length - 1));
    let length = 0;
    for (let i = 1; i <= targetIndex; i++) {
      length += lut[i].sub(lut[i - 1]).length;
    }
    return length;
  }

  lerpPoints(count: number): Point[] {
    const points = [];
    for (let i = 0; i < count; i++) {
      points.push(this.pointAt(i / (count - 1)));
    }
    return points;
  }

  divisionPoints(count: number): Point[] {
    const points = [];
    for (let i = 1; i < count; i++) {
      points.push(this.pointAt(i / count));
    }
    return points;
  }

  pointAt(t: number): Point {
    const { center, rx, ry } = this.computeCenterAndRadii();
    const phi = this.xRotation.radians;
    const cosPhi = Math.cos(phi);
    const sinPhi = Math.sin(phi);

    // Parametric angles
    const startAngle = this.parametricAngle(this.start);
    const endAngle = this.parametricAngle(this.end);
    let deltaAngle = endAngle - startAngle;
    if (this.sweep && deltaAngle < 0) {
      deltaAngle += 2 * Math.PI;
    } else if (!this.sweep && deltaAngle > 0) {
      deltaAngle -= 2 * Math.PI;
    }

    const angle = startAngle + t * deltaAngle;
    const sinAngle = Math.sin(angle);
    const cosAngle = Math.cos(angle);

    // Compute point in the rotated coordinate system
    const xPrime = rx * cosAngle;
    const yPrime = ry * sinAngle;

    // Rotate point back to original coordinate system and translate to center
    const x = xPrime * cosPhi - yPrime * sinPhi + center.x;
    const y = xPrime * sinPhi + yPrime * cosPhi + center.y;

    return point(x, y);
  }

  // #endregion

  // #region Interpolation

  lerp(t: number, end: this): this {
    // Interpolate geometric properties
    const start = this.start.lerp(t, end.start);
    const endPoint = this.end.lerp(t, end.end);
    const radii = this.radii.lerp(t, end.radii);
    const xRotation = this.xRotation.lerp(t, end.xRotation);

    // Dynamically compute flags based on interpolated geometry
    const angularSpan = this.getAngularSpan(start, endPoint, radii, xRotation);
    const largeArc = angularSpan > Math.PI;

    const sweep = this.getSweepDirection(start, endPoint, radii, xRotation);

    return ellipse(start, endPoint, radii, xRotation, largeArc, sweep) as this;
  }

  private getSweepDirection(
    start: Point,
    end: Point,
    radii: Point,
    xRotation: Angle
  ): boolean {
    const phi = xRotation.radians;

    // Transform start and end to parametric space
    const startAngle = this.parametricAngle2(start, radii, phi);
    const endAngle = this.parametricAngle2(end, radii, phi);

    // Determine direction of sweep
    return endAngle >= startAngle;
  }

  private getAngularSpan(
    start: Point,
    end: Point,
    radii: Point,
    xRotation: Angle
  ): number {
    const phi = xRotation.radians;

    // Transform start and end to parametric space
    const startAngle = this.parametricAngle2(start, radii, phi);
    const endAngle = this.parametricAngle2(end, radii, phi);

    // Compute angular span
    let angularSpan = endAngle - startAngle;
    if (angularSpan < 0) {
      angularSpan += 2 * Math.PI;
    }

    return angularSpan;
  }

  interp(end: this): (t: number) => this {
    return (t: number) => this.lerp(t, end);
  }

  // #endregion

  // #region Serialization

  toJSON(): unknown {
    return [
      this.start.toJSON(),
      this.end.toJSON(),
      this.radii.toJSON(),
      this.xRotation.toJSON(),
      this.largeArc,
      this.sweep
    ];
  }

  static fromJSON(json: unknown): Ellipse {
    return EllipseSchema.parse(json);
  }

  // #endregion

  // #region SVG Conversion

  /**
   * Converts the ellipse parameters to an SVG path data string.
   *
   * @param {number} [fractionDigits] - The number of decimal places to include in the output. If not provided, the default precision will be used.
   * @returns {string} The SVG path data string representing the ellipse as an arc.
   */
  toSvgPathData(fractionDigits?: number): string {
    return svgPathDataFromEndpointsEllipse(this, fractionDigits);
  }

  /**
   * Creates an `Ellipse2` instance from SVG path data.
   *
   * @param pathData - The SVG path data string containing the move and arc commands.
   * @returns An `Ellipse2` instance created from the provided SVG path data.
   */
  static fromSvgPathData(pathData: string): Ellipse {
    const params = endpointsEllipseFromSVGPathData(pathData);
    return Ellipse.fromEndpointsEllipse(params);
  }

  // #endregion

  // #region String Conversion

  toString(fractionDigits?: number): string {
    return composeStruct([
      ['start', this.start.toString(fractionDigits)],
      ['end', this.end.toString(fractionDigits)],
      ['radii', this.radii.toString(fractionDigits)],
      ['xRotation', this.xRotation.toStringDegrees(fractionDigits)],
      ['largeArc', this.largeArc],
      ['sweep', this.sweep]
    ]);
  }
}

function _speed(a: number, b: number, t: number, phi: number): number {
  // Speed along the parametric ellipse
  const cosPhi = Math.cos(phi);
  const sinPhi = Math.sin(phi);

  const dx = -a * Math.sin(t) * cosPhi - b * Math.cos(t) * sinPhi;
  const dy = -a * Math.sin(t) * sinPhi + b * Math.cos(t) * cosPhi;

  return Math.sqrt(dx * dx + dy * dy);
}
